// Core
import React from 'react';
import { Box, Flex, Grid, Text, Image, Tooltip, IconButton, useBreakpointValue, useColorMode, useColorModeValue } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';

// Assets
import { ReactComponent as RocketIcon } from 'assets/svg/rocket.svg';
import { ReactComponent as CheckIcon } from 'assets/svg/current-month-check.svg';
import { formatDateWithSuffix } from 'services/Utils';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';

// Components
import Card from 'components/Card/Card';
import MembershipPrivilegeItem from 'components/Membership/MembershipPrivilegeItem';
import { Info2Icon } from 'components/Icons/Icons';

const PrivilegeMonth = ({ start, entries, current, isDark }) => {
	const fontSize = entries > 9999 
		? '12px' : entries > 999 
		? '14px' : entries > 99 
		? '18px' : '24px';

	const entriesCount = entries > 9999 
		? '9999+' : entries > -1
		? entries : 0;

	return (
		<Box 
			w="52px"
			h="52px"
			borderRadius="full"
			border="1px solid #2ABDFD"
			bg={ isDark ? '#0D234D' : 'white' }
			p="3.5px"
			position="relative"
			boxShadow={ isDark ? '0px 1px 10px 0px rgba(9, 155, 255, 0.60)' : 'unset'}>
  			<Box
  				py="9.5px"
  				w="43px"
  				h="43px"
  				borderRadius="100px"
					bg={ !current ? isDark ? '#0D234D' : '#CBE7FF' : 'linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)'}>
  				{ start ? (<RocketIcon fontSize="24px" style={{marginLeft: '9px'}} />) 
  				: (
	  				<Text
						fontSize={fontSize}
						fontStyle="italic"
						fontWeight="800"
						lineHeight="34px"
						letterSpacing="-0.4px"
					    height="24px"
					    textAlign="center"
					    marginTop="-5px"
					    marginBottom="5px"
					    color="white"
	  				>{entriesCount}</Text>
	  			)}
  			</Box>

  			{ current && (<CheckIcon style={{position: 'absolute', right: '-4px', top: '-4px'}} />)}

  		</Box>
	);
};

const PrivilegeMonthDateLine = ({ progress, month, isMonthly, isDark }) => {
	return (
		<Box
  			w="100%"
  			h="4px"
  			overflow="visible"
  			borderRadius="21px"
  			position="relative"
  			bg={ isDark ? '#0D234D' : '#CBE7FF' }
  			_before={{
  				content: '""',
  				position: 'absolute',
  				left: '0px',
  				top: '0px',
  				bg: 'linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)',
  				width: progress > 100 ? '100' + '%' : progress + '%',
  				height: '100%',
  				borderRadius: '10px',
  				zIndex: '1'
  			}}
			>
			<Box
				zIndex="2" 
				position="relative"
				w="73px" 
				m="-8px auto 0 auto"
				borderRadius="full"
				bg={ isDark ? 'white' : '#0E1633' }
				h="20px">
				<Text
					textAlign="center"
					fontSize="15px"
					color="#099BFF"
					fontStyle="italic"
					fontWeight="800"
					lineHeight="20px"
					letterSpacing="-0.4px"
					>
					{month} { isMonthly ? 'month' : month > 1 ? 'years' : 'year'}
				</Text>
			</Box>

			{ isDark && <Box bg="#099BFF" filter="blur(38px)" w="100%" h="36px" position="absolute" top="-85px" /> }
		</Box>
	);
};

const PrivilegePlan = ({userData, countActivePlans, petrolSummary}) => { 
  const item = userData?.activePlan || {entries: {monthNumber: 0, prev: 0, now: 0, nextPeriodProgress: 0}};

  item['entries'].prev = item?.entries?.monthNumber === 0 ? 
  	0 
  	: 
  	item?.entries?.now - (item?.entries?.next - item?.entries?.now);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const { colorMode, toggleColorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const editLineBgDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 42.74%, #FFF 50.39%, #38A9E1 57.37%, rgba(56, 169, 225, 0.00) 100%)';
  const editLineBgWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  const editLineBg = useColorModeValue(editLineBgWhite, editLineBgDark);
  const ttpTextColor = useColorModeValue('#0E1633', '#E1E1E1');
  const ttpBgColor = useColorModeValue('white', '#0D234D');

  return (
    <Box
    	borderRadius="0 0 16px 16px" 
    	position="relative" 
    	borderLeft="1px solid #2ABDFD"
    	borderRight="1px solid #2ABDFD"
    	borderBottom="1px solid #2ABDFD"
    	overflow="hidden">
    { /*<>
		<Box p="16px 19px">
			{ isDark ? <Lines className="abstract-lines" style={{zIndex: '-1'}} /> 
				: <LinesWhite className="abstract-lines" style={{zIndex: '-1', opacity: 0.3}} /> 
			}

			<Flex gap="24px" alignItems="center">
				{ !isMobile ? (
					<>
						<PrivilegeMonth 
							isDark={isDark}
							entries={ item?.entries?.monthNumber > 0 ? item?.entries?.prev : item?.entries?.now }
							start={ item?.entries?.monthNumber === 0 ? true : false }
							current={true} />
						<PrivilegeMonthDateLine 
							isDark={isDark}
							progress={item?.entries?.monthNumber > 0 ? 100 : item?.entries?.nextPeriodProgress}
							isMonthly={ item?.paymentType === 'monthly' ? true : false } 
							month={ item?.entries?.monthNumber > 0 ? item?.entries?.monthNumber - 1 : 1} />

						<PrivilegeMonth 
							isDark={isDark}
							entries={ item?.entries?.monthNumber > 0 ? item?.entries?.now : item?.entries?.next }
							current={item?.entries?.monthNumber > 0 ? true : false} />
						<PrivilegeMonthDateLine 
							isDark={isDark}
							progress={item?.entries?.monthNumber === 0 ? 0 : item?.entries?.nextPeriodProgress}
							isMonthly={ item?.paymentType === 'monthly' ? true : false } 
							month={item?.entries?.monthNumber > 0 ? item?.entries?.monthNumber : 2} />

						<PrivilegeMonth
							isDark={isDark} 
							entries={ item?.entries?.monthNumber > 0 ? 
								item?.entries?.next 
								: 
								item?.entries?.next + (item?.entries?.next - item?.entries?.now) }
							current={
								item?.entries?.monthNumber > 0 &&
								item?.entries?.nextPeriodProgress >= 100 ? true : false} />
					</>
				) : (
					<>
						<PrivilegeMonth
							isDark={isDark} 
							entries={ item?.entries?.now }
							current={true} />
						<PrivilegeMonthDateLine 
							isDark={isDark}
							progress={item?.entries?.nextPeriodProgress || 0} 
							isMonthly={ item?.paymentType === 'monthly' ? true : false } 
							month={item?.entries?.monthNumber + 1} />
						<PrivilegeMonth
							isDark={isDark} 
						    entries={ item?.entries?.next }
							current={ item?.entries?.nextPeriodProgress >= 100 ? true : false } />
					</>
				)}
			</Flex>
		</Box>

		<Box bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)" p="4px 8px" w="100%" textAlign="center">
			<Flex gap="2px" alignItems="center" w="100%" justifyContent="center">
				<Text variant="title-md" fontStyle="italic" color="white" whiteSpace="nowrap">
					{ countActivePlans > 1 ? 
						`TOTAL ENTRIES AMOUNT: ${userData?.entries}` 
						: `ENTRIES INCLUDED: ${userData?.entries}`
					}
				</Text>

				{ countActivePlans > 1 &&
					<Tooltip 
	          label="Shows combined benefits across all active subscriptions" 
	          bg={ttpBgColor}
	          color={ttpTextColor}
	          fontWeight="400"
	          fontSize="12px"
	          borderRadius="4px"
	          maxW="198px"
	          padding="4px 8px"
	          aria-label="tooltip" 
	          hasArrow>
	          <IconButton
	              icon={<Info2Icon fontSize="18px" />}
	              variant="ghost-sx"
	              size="sm"
	              w="8px"
	              aria-label=""
	            />
		      </Tooltip>
		    }
			</Flex>
		</Box>
	  </>*/}

		<Grid bg={ isDark ? '#0a1027' : 'white' } rowGap="10px" p="16px" templateColumns={{ sm: '1fr', md: '1fr 1fr' }} gap="24px" templateRows='1fr'>
			<MembershipPrivilegeItem 
			  isPopular={true}
			  title={`${userData?.activeSubscription?.plan?.entries || 0} FREE`} 
			  description={`${userData?.activeSubscription?.plan?.paymentType === 'yearly' ? 'Entries into Every Giveaway' 
			  	: userData?.activeSubscription?.plan?.entries > 1 ? 'Entries Into EVERY Draw Each Month' : 'Entry Into EVERY Draw Each Month'}`} 
			/>
			{ item?.type !== 'fuel' && 
			<>
				<MembershipPrivilegeItem isPopular={true} title={`${item?.matesRates}% Access`} description="to Mates Rates Discounts" />
				<MembershipPrivilegeItem isPopular={true} title={`"${item?.name}"`} description="Discounts with Selected Partners" />
				<MembershipPrivilegeItem isPopular={true} title={`${item?.merchDiscount}%`} description="Off All LMCT+ Merchandise" />
			</>
			}
			<MembershipPrivilegeItem isPopular={true} title={
				item?.vipInvites === 'none' ? '' : item?.vipInvites === 'vip' ? 'VIP' : 'VIP+'
			} description={'Invites to LMCT+ Events and Games Across Australia'} />

			{ item?.type === 'fuel' && 
				<MembershipPrivilegeItem isPopular={true} title={`${petrolSummary?.rules?.fuel?.giveaway[0] || 0} Giveaway`} description={`auto entry per ${petrolSummary?.rules?.fuel?.giveaway[1] || 0} L`} />
			}

			<MembershipPrivilegeItem isPopular={true} title="" description="LMCT+ Early Bird Promotion Emails" />
			{ item?.freeHat && (
				<MembershipPrivilegeItem isPopular={true} title="Free" description="LMCT+ Hat!" />
			)}
		</Grid>

		<Box 
			w="100%"
			h="2px"
			bg={editLineBg}
			position="absolute"
			bottom="0" />
    </Box>
  );
};

export default PrivilegePlan;